import type { HTMLAttributes, PropsWithChildren, Ref } from 'react';
import type React from 'react';

/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';

import type { A11yTextType } from '../../../../common/components/A11yTextWrapper/A11yTextWrapper';
import type { ProductListingItemData } from './ProductListingItem.types';

import { Helpers } from '../../../../../utility/helpers';
import CloudinaryImage from '../../../../common/components/atoms/CloudinaryImage';
import ProductBadge from '../../../../common/components/atoms/ProductBadge';
import { mapProductAttributeToProps } from '../../../../common/utils/productUtils';
import { EnergyLabelContent } from '../../../../ecommerce/components/EnergyLabel';
import { ProductInfoProperty } from '../../../../ecommerce/components/ProductInfoProperty';
import { getProductPackageSizeInfoText } from '../../../../ecommerce/components/ProductInfoProperty/ProductInfoProperty.utils';
import { useMissingProductFallbackImage } from '../../../../ecommerce/hooks/useMissingProductImage';
import styles from './ProductListingItem.module.scss';

interface ProductListingItemProps {
    className?: string;
    product: ProductListingItemData;
    a11yLabel: string | undefined;
    a11yType: A11yTextType;
    a11yDescribedBy?: string;
    handleLinkClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
    linkRef?: Ref<HTMLAnchorElement>;
    /**
     * React 19 ref-as-prop; also used by GA view_item_list (Intersection Observer)
     * via BuyableSearchProduct → ProductSearchListingItem.
     */
    ref?: Ref<HTMLDivElement>;
    isALink: boolean;
    imageDimensions?: { width: number; height: number };
    topSlot?: React.ReactNode;
    imageSlot?: React.ReactNode;
    /** Optional first badge before attribute badges (e.g. EDLP pricing splash). */
    leadingBadgeSlot?: React.ReactNode;
}

const Container = ({ ref, linkRef, ...props }: PropsWithChildren<ProductListingItemProps>) => {
    const imgDimension = props.imageDimensions || { width: 72, height: 72 };

    const packageSizeInformationText = getProductPackageSizeInfoText(props.product);

    const attributes = props.product.attributeLabels
        .map(mapProductAttributeToProps)
        .filter(Boolean);

    const fallbackImageUrl = useMissingProductFallbackImage();

    return (
        <div
            ref={ref}
            className={classNames(styles.Container, props.className)}
            data-testid="product-listing-item"
        >
            {props.topSlot}
            <div
                className={classNames(
                    styles.Content,
                    props.isALink ? styles.FocusIndicator : undefined,
                )}
            >
                <div className={styles.Info}>
                    <div className={styles.Images}>
                        <div className={styles.ImageSlot}>
                            {props.imageSlot}
                            <div aria-hidden={props.a11yType === 'ariaHidden'}>
                                <CloudinaryImage
                                    url={props.product.imageUrl}
                                    name={props.product.name}
                                    fallbackUrl={fallbackImageUrl}
                                    width={imgDimension.width}
                                    height={imgDimension.height}
                                    transform={`f_auto,fl_clip,fl_progressive,q_auto,c_lpad,g_center,h_${imgDimension.height},w_${imgDimension.width}`}
                                    retinaTransform={`f_auto,fl_clip,fl_progressive,q_auto,c_lpad,g_center,h_${imgDimension.height * 2},w_${imgDimension.width * 2}`}
                                    lazy
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.BaseInfo}>
                        {props.isALink ? (
                            <a
                                ref={linkRef}
                                className={classNames(styles.Name, styles.Link)}
                                href={props.product.url}
                                onClick={props.handleLinkClick}
                                aria-label={props.a11yLabel}
                                aria-describedby={props.a11yDescribedBy}
                            >
                                {props.product.name}
                            </a>
                        ) : (
                            <div
                                className={styles.Name}
                                aria-hidden={props.a11yType === 'ariaHidden'}
                            >
                                {props.product.name}
                            </div>
                        )}

                        <div
                            className={styles.Description}
                            aria-hidden={props.a11yType === 'ariaHidden'}
                        >
                            {props.product.manufacturer && (
                                <span className={styles.DescriptionBolded}>
                                    {Helpers.appendDot(props.product.manufacturer)}
                                </span>
                            )}

                            {packageSizeInformationText?.visual && (
                                <span>{Helpers.appendDot(packageSizeInformationText.visual)}</span>
                            )}
                        </div>

                        <ProductInfoProperty.CustomerInformationText
                            product={props.product}
                            a11yType={props.a11yType}
                        />

                        {(props.leadingBadgeSlot || !!attributes.length) && (
                            <ul
                                className={styles.Badges}
                                aria-label="Märkt med"
                                aria-hidden={props.a11yType === 'ariaHidden'}
                            >
                                {props.leadingBadgeSlot && (
                                    <li className={styles.LeadingBadge}>
                                        {props.leadingBadgeSlot}
                                    </li>
                                )}
                                {attributes.map((badge) => (
                                    <ProductBadge
                                        key={badge.id}
                                        as="li"
                                        zoomable={false}
                                        url={badge.url!}
                                        alt={badge.alt}
                                        dimensions={{ width: 32, height: 32 }}
                                    />
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
            {props.product.energyLabel ? (
                <div className={styles.EnergyAndPrices}>
                    <EnergyLabelContent
                        energyLabel={props.product.energyLabel}
                        productUrl={props.product.url}
                        size="small"
                    />
                    {props.children}
                </div>
            ) : (
                props.children
            )}
        </div>
    );
};

const ProductListingItem = {
    Container,
    PricingContainer: (props: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => (
        <div {...props} className={classNames(styles.PricingContainer, props.className)}>
            {props.children}
        </div>
    ),
    PrimaryPricing: (props: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => (
        <div {...props} className={classNames(styles.PrimaryPricing, props.className)}>
            {props.children}
        </div>
    ),
    SecondaryPricing: (props: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => (
        <div {...props} className={classNames(styles.SecondaryPricing, props.className)}>
            {props.children}
        </div>
    ),
    VariantSelector: (props: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => (
        <div {...props} className={classNames(styles.VariantSelector, props.className)}>
            {props.children}
        </div>
    ),
};

export default ProductListingItem;
